import {PropsWithChildren} from 'react';
import {Divider, IconButton, List, ListItem, ListItemIcon, ListItemText, Menu, MenuItem} from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import * as MaterialUIIcons from '../../controls/MaterialUIIcons';
import {GridColDef} from '@mui/x-data-grid-pro';
import {SortableContainer, SortableElement, SortableHandle} from 'react-sortable-hoc';

const LIST_ITEM_HEIGHT = 40;
const PAPER_TOP_BOTTOM_PADDING = 8;

const useStyles = makeStyles((theme) =>
    ({
        paper: (props: { maxSpace: number }) => ({
            marginTop: 4,
            maxHeight: `calc( 100vh - ${props.maxSpace + 3}px )`,
        }),
        colName: {
            minWidth: '150px',
        },
        listItem: {
            '&:hover, body>&': {
                backgroundColor: theme.palette.action.hover,
            },
            transition: theme.transitions.create('background-color', {
                duration: theme.transitions.duration.shortest,
            }),
            height: LIST_ITEM_HEIGHT,
            zIndex: theme.zIndex.modal
        }
    })
);

export interface CustomGridColDef extends GridColDef {
    overviewHeader?: string;
}

type Props = {
    updateColumns: (columns: GridColDef[]) => void,
    columns: CustomGridColDef[];
    reset(): void;
    testId?: string;
    anchorEl?: Element;
    open: boolean;
    isTreeView: boolean;
    onClose(): void;
    autoPlacement?: boolean;
};

export default function AdminDataGridColumnsMenu(props: PropsWithChildren<Props>) {

    function tuggleColumnVisibility(column: GridColDef) {
        const columns = props.columns.map(i => {
            return {
                ...i,
                hide: i.field === column.field ? !i.hide : i.hide
            };
        });

        props.updateColumns(columns);
    }

    const firstCol = props.columns[0];

    function onSortEnd({oldIndex, newIndex}: { oldIndex: number, newIndex: number }) {
        if (oldIndex === newIndex) {
            return;
        }

        if (props.isTreeView && newIndex === 0)
            newIndex = 1;

        const columns = props.columns.map(i => ({...i}));

        let column = columns[oldIndex];
        columns.splice(oldIndex, 1);
        columns.splice(newIndex, 0, column);

        props.updateColumns(columns);
    }

    const DragHandle = SortableHandle(() => (<MaterialUIIcons.DragIndicatorIcon fontSize='small'/>));

    const SortableItem = SortableElement<{ column: CustomGridColDef }>(({column}: { column: CustomGridColDef }) => (
        <ListItem tabIndex={0} className={classes.listItem}>
            <ListItemIcon>
                <IconButton
                    color="primary"
                    disabled={column.field === firstCol.field && props.isTreeView}
                    onClick={(e) => {
                        e.preventDefault();
                        tuggleColumnVisibility(column);
                    }}
                >
                    {
                        column.hide
                            ? <MaterialUIIcons.VisibilityOffIcon fontSize='small'/>
                            : <MaterialUIIcons.VisibilityIcon fontSize='small'/>
                    }
                </IconButton>
            </ListItemIcon>
            <ListItemText className={classes.colName}
                          primary={column.overviewHeader ? column.overviewHeader : column.headerName}/>
            <DragHandle/>
        </ListItem>
    ));


    const SortableList = SortableContainer< { columns: GridColDef[] }>(({columns}: { columns: GridColDef[] }) => {
        return (
            <List>
                {columns.map((column, index) => (
                    <SortableItem disabled={firstCol.field === column.field && props.isTreeView} key={column.field}
                                  index={index} column={column}/>
                ))}
            </List>
        );
    });

    function getOffset(): { top: number | undefined, maxSpace: number } {
        const rect = props.anchorEl?.getBoundingClientRect();
        if (!rect) return {maxSpace: 0, top: undefined};
        const topOffset = window.pageYOffset + rect.bottom;
        const paperHeight = ((props.columns.length + 1) * LIST_ITEM_HEIGHT) + PAPER_TOP_BOTTOM_PADDING * 4;
        const bottomDistance = window.innerHeight - (window.pageYOffset + rect.bottom);
        const isOnTop = bottomDistance < paperHeight;
        return {
            top: isOnTop && props.autoPlacement ? topOffset - paperHeight - rect.height : undefined,
            maxSpace: isOnTop && props.autoPlacement ? bottomDistance : topOffset
        };
    }

    const rect = getOffset();

    const classes = useStyles({maxSpace: rect.maxSpace});
    return (
        <Menu
            className={classes.paper}
            elevation={1}
            anchorOrigin={{vertical: 'bottom', horizontal: 'right'}}
            transformOrigin={{vertical: 'top', horizontal: 'right'}}
            id="columns-context-menu"
            anchorEl={props.anchorEl}
            open={props.open}
            onClose={props.onClose}
            PaperProps={{sx: {top: rect.top ? `${rect.top}px !important` : undefined}}}
        >
            <SortableList columns={props.columns} onSortEnd={onSortEnd} lockAxis="y" useDragHandle
                          lockToContainerEdges={true} lockOffset={['0%', '-100%']}/>
            <Divider/>
            <MenuItem onClick={(e) => {
                e.preventDefault();
                props.reset();
                props.onClose();
            }}>
                <ListItemIcon>
                    <MaterialUIIcons.AutorenewIcon fontSize="small"/>
                </ListItemIcon>
                <ListItemText primary="Tilbakestill"/>
            </MenuItem>
        </Menu>
    );
}

